import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	forwardRef,
	inject,
	input,
	OnDestroy,
	OnInit,
	signal,
	untracked,
} from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	FormBuilder,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
	ValidationErrors,
	Validator,
	Validators,
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatOption } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoModule } from '@jsverse/transloco';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { Address, GetAddress } from '../../../api/app-types/address.app';
import { ADDRESS_COMPLETION_SERVICE, AddressCompletionService } from '../../../api/services/address-completion.service';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { postalCodeAndCityValidator } from '../../../shared/validators/postalcode-city-validator';
import { LvrInputDirective } from '../../directives/lvr-input.directive';
import { LvrField } from '../field/field';
import { toSignal } from '@angular/core/rxjs-interop';
import { germanPostalCodeCheck } from '../../../shared/helpers/german-postalcode.func';
import { buildAddress } from '../../../shared/helpers/build-address.func';
import { Match } from '../../../api/app-types/match.app';

@Component({
	selector: 'lvr-address-completion',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatFormFieldModule,
		MatAutocompleteModule,
		TranslocoModule,
		ReactiveFormsModule,
		MatOption,
		MatInputModule,
		LvrInputDirective,
		LvrField,
		LvrAppendAsteriskPipe,
	],
	providers: [
		{
			provide: ADDRESS_COMPLETION_SERVICE,
			useClass: AddressCompletionService,
		},
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => AddressCompletion),
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => AddressCompletion),
			multi: true,
		},
	],
	templateUrl: './address-completion.html',
	styleUrl: './address-completion.scss',
})
export class AddressCompletion implements ControlValueAccessor, Validator, OnInit, OnDestroy {
	public readonly form = inject(FormBuilder).group({
		streetAndHouseNumber: [''],
		postalCodeAndCity: ['', postalCodeAndCityValidator()],
	});

	public readonly showAsterisk = input<boolean>(false);
	public readonly checkForDuplicatedAddress = input<boolean>(false);
	public readonly duplicatedAddressHintKey = input<string>('base.form.warning.duplicated_address_institution');
	public readonly duplicatedAddressLink = input<string>('/desk/institution/');
	public readonly excludeId = input<number | undefined>(undefined);
	public readonly duplicatedAddressCheckerAsync =
		input<(address: Address, excludeId?: number) => Promise<Match | undefined>>();

	private addressParts = computed(
		(): Address => ({
			id: 0,
			streetAndHouseNumber: this.streetValue() ?? '',
			postalCodeAndCity: this.postalValue() ?? '',
			anonym: '',
			identifier: '',
			soundexStreet: '',
		}),
	);
	private readonly addressKey = computed(() => buildAddress(this.addressParts()));
	private readonly _duplicateMatch = signal<Match | undefined>(undefined);
	public readonly duplicateMatch = computed(() => this._duplicateMatch());

	private addressService = inject(ADDRESS_COMPLETION_SERVICE);

	private _value: GetAddress | null = null;
	// ControlValueAccessor callbacks
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onChange = (_value: GetAddress | null) => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onTouched = () => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onValidatorChange = () => {};

	private _isDisabled = false;

	public get disabled() {
		return this._isDisabled;
	}

	private readonly postalCtrl = this.form.controls.postalCodeAndCity;
	private readonly postalValue = toSignal(this.postalCtrl.valueChanges, {
		initialValue: this.postalCtrl.value,
	});

	private readonly streetCtrl = this.form.controls.streetAndHouseNumber;
	private readonly streetValue = toSignal(this.streetCtrl.valueChanges, {
		initialValue: this.streetCtrl.value,
	});

	public isNotAGermanPostalCode = signal<boolean>(false);
	public invalidFormat = signal<boolean>(false);

	public readonly showWarning = computed(() => !this.invalidFormat() && this.isNotAGermanPostalCode());
	public readonly showDuplicatedWarning = computed(() => this.checkForDuplicatedAddress() && !!this.duplicateMatch());

	public constructor() {
		this.handleGermanPostalCodeCheck();
		this.handleDuplicatedAddress();
		this.handleRequiredValidators();
	}

	private handleGermanPostalCodeCheck() {
		effect(() => {
			const value = this.postalValue();
			this.isNotAGermanPostalCode.set(!germanPostalCodeCheck(value));
			this.invalidFormat.set(this.postalCtrl.hasError('invalidPostalCodeAndCity'));
		});
	}

	private handleDuplicatedAddress() {
		effect(async (onCleanup) => {
			if (!this.checkForDuplicatedAddress()) {
				this._duplicateMatch.set(undefined);
				return;
			}

			const key = this.addressKey();
			if (!key) {
				this._duplicateMatch.set(undefined);
				return;
			}

			const checker = this.duplicatedAddressCheckerAsync();
			if (!checker) {
				this._duplicateMatch.set(undefined);
				return;
			}

			// On input changes cleanup marks this run as cancelled
			let cancelled = false;
			onCleanup(() => {
				cancelled = true;
			});

			const hit = await checker(this.addressParts(), this.excludeId());
			if (!cancelled) this._duplicateMatch.set(hit);
		});
	}

	public suggestions = signal<GetAddress[]>([]);
	public readonly displayAddressFields = signal<boolean>(false);
	private readonly debounceTimeout = signal<number | undefined>(undefined);

	private _destroy = new Subject<void>();

	public ngOnInit(): void {
		this.registerValueChanges();
		this.form.statusChanges.pipe(takeUntil(this._destroy)).subscribe(() => this._onValidatorChange());
	}

	public writeValue(obj: GetAddress | null): void {
		this._value = obj;
		this._onChange(obj);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		this.form.patchValue(obj as any, { emitEvent: false });
	}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public registerOnChange(fn: any): void {
		this._onChange = fn;
	}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public registerOnTouched(fn: any): void {
		this._onTouched = fn;
	}
	public validate(_: AbstractControl): ValidationErrors | null {
		return this.form.valid ? null : { addressInvalid: true };
	}
	public registerOnValidatorChange(fn: () => void): void {
		this._onValidatorChange = fn;
	}
	public setDisabledState?(isDisabled: boolean): void {
		this._isDisabled = isDisabled;
	}

	public ngOnDestroy(): void {
		this._destroy.next();
		this._destroy.complete();
		if (untracked(this.debounceTimeout)) {
			clearTimeout(untracked(this.debounceTimeout));
		}
	}

	private handleRequiredValidators() {
		effect(() => {
			const required = this.showAsterisk();

			if (required) {
				this.streetCtrl.addValidators(Validators.required);
				this.postalCtrl.addValidators(Validators.required);
			} else {
				this.streetCtrl.removeValidators(Validators.required);
				this.postalCtrl.removeValidators(Validators.required);
			}

			this.streetCtrl.updateValueAndValidity({ emitEvent: false });
			this.postalCtrl.updateValueAndValidity({ emitEvent: false });
			this._onValidatorChange();
		});
	}

	public get hasRequiredError() {
		if (!this.showAsterisk()) return false;

		const streetCtrl = this.form.controls.streetAndHouseNumber;
		const postalCtrl = this.form.controls.postalCodeAndCity;

		const touchedOrDirty = streetCtrl.touched || streetCtrl.dirty || postalCtrl.touched || postalCtrl.dirty;

		const hasRequired = streetCtrl.hasError('required') || postalCtrl.hasError('required');

		return hasRequired && touchedOrDirty;
	}

	/**
	 * Checks if a form control has a specific validation error and has been touched or is dirty.
	 * Used for LVR-Error.
	 * @param control The form control to check.
	 * @param error The name error to look for.
	 * @returns True if the error exists and the control was touched.
	 */
	public hasError = (control: AbstractControl | null, error: string) => {
		return (control?.hasError(error) && (control?.touched || control?.dirty)) ?? false;
	};

	private registerValueChanges() {
		this.form.valueChanges.pipe(takeUntil(this._destroy), distinctUntilChanged()).subscribe((value) => {
			const address = (value as GetAddress) ?? (value as GetAddress);
			if (!address) return;

			this.updateSuggestions(address);
			this.writeValue(address);
		});
	}

	public displayAddress(address: GetAddress | string) {
		if (!address) return '';
		if (typeof address === 'string') return address;
		return `${address.streetAndHouseNumber} - ${address.postalCodeAndCity}`;
	}

	private updateSuggestions(address: GetAddress) {
		const search = `${address.streetAndHouseNumber} - ${address.postalCodeAndCity}`;
		if (!search || search.length < 2) {
			this.suggestions.set([]);
			return;
		}

		const delay = 300;
		if (untracked(this.debounceTimeout)) {
			clearTimeout(untracked(this.debounceTimeout));
		}
		const timeout = window.setTimeout(() => {
			this.addressService.suggest(search).then((list) => {
				this.suggestions.set(list);
			});
		}, delay);
		this.debounceTimeout.set(timeout);
	}

	public addressSelected(event: MatAutocompleteSelectedEvent) {
		const selected: GetAddress = event.option.value;
		this.form.patchValue({
			streetAndHouseNumber: selected.streetAndHouseNumber,
			postalCodeAndCity: selected.postalCodeAndCity,
		});
		this.suggestions.set([]);
	}
}
